'use client';

import { useRef, useState } from 'react';

interface FileDropzoneProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

export function FileDropzone({ onFiles, disabled }: FileDropzoneProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.name.endsWith('.csv')
    );
    if (files.length > 0) onFiles(files);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) onFiles(files);
    // reset so the same file can be re-picked
    e.target.value = '';
  }

  return (
    <div
      className={`rounded-2xl border-2 border-dashed transition-all cursor-pointer select-none ${
        dragging
          ? 'border-indigo-400 bg-indigo-50'
          : 'border-slate-300 bg-slate-50 hover:border-indigo-300 hover:bg-indigo-50/30'
      } ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <div className="flex flex-col items-center justify-center gap-2 py-8 px-6 text-center">
        <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-sm">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18M3 15h18M9 3v18" />
          </svg>
        </div>
        <p className="text-sm font-semibold text-slate-700">
          Drop CSV files here, or <span className="text-indigo-600">click to browse</span>
        </p>
        <p className="text-[11px] text-slate-400 max-w-xs leading-relaxed">
          Auto-detects WIP, Supply/AWP, and Schedule files. All data stays in your browser — nothing is uploaded to a server.
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        multiple
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
      />
    </div>
  );
}
